/**
 * A table draws its first fifty rows; the rest leave as a CSV or an Excel workbook —
 * `docs/adr/9008`, and ADR 9010 as amended 2026-10-01 (DEC-98) for the second format.
 *
 * Planner review, 2026-09-25: a screen that draws every row of a collection is not more
 * complete for it, only longer. Past a few dozen rows nobody reads a table by scrolling —
 * they sort it in a spreadsheet — and a thousand-row `<tbody>` pushes everything below it
 * off the page. So a table draws the head of its ordered list and says, above it, how many
 * rows it is not drawing and where they are.
 *
 * **Above the table, at the inline end** (owner feedback F7, 2026-09-27): a download is a
 * table's toolbar, not its footnote. Below a fifty-row table it was the last thing reached —
 * by eye and by Tab — so the notice renders as a toolbar row directly over the table, aligned
 * to the inline end, and every caller places it there.
 *
 * **A presentation bound, never a data bound.** Every total, count and tile on the screen is
 * still computed over all the rows; `capRows` only decides which of them are DRAWN. The
 * consolidation option directory worked this way first (`OPTION_ROWS_DRAWN`), and this is
 * the same rule made shared.
 *
 * **The CSV is the table, not the route.** A route registers ONE export and several routes
 * draw more than one long table, so a download wired to the route's export would hand the
 * planner a different table from the one that was cut. The caller passes the request for
 * THIS table — built from the same rows, in the same order, the table draws — and the
 * download goes through `runExport` like every other export, so provenance, raw numbers and
 * the missing-data states survive exactly as they do on the route's own export (G2.4).
 */

import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpen, Download } from 'lucide-react';

import { arrangeDownloads, runColumnGuide, runExport, saveOutcome, type ColumnGuideSource, type DownloadArrangement, type ExportRequest } from '../export';
import { Button } from '../primitives';

import { DownloadProgressLine, useDownloadProgress, writtenProgress, type DownloadProgressState, type DownloadReporter } from './download-progress';
import { OtherCsvLink } from './other-csv-link';

/** How many rows a table draws before the rest go to the CSV. */
export const TABLE_ROW_CAP = 50;

export interface CappedRows<T> {
  /** The rows to draw — the head of `rows`, in its order. */
  readonly shown: readonly T[];
  /** Every row, drawn or not. */
  readonly total: number;
}

/** The head of an already-ordered list. Sort BEFORE capping, or the head is arbitrary. */
export function capRows<T>(rows: readonly T[], cap: number = TABLE_ROW_CAP): CappedRows<T> {
  return { shown: rows.length > cap ? rows.slice(0, cap) : rows, total: rows.length };
}

/**
 * What a download holds, when it is not "the rows the table counts": the catalogue key of each format's
 * button and the figures it names (`{served}`, `{dropped}` …) beside the `{total}` every button has.
 * A file that leaves lines out of its table (the dropped doors', which CSV drops and Excel keeps on a
 * sheet of its own) says what it holds on the button — a count the planner reads before the file.
 */
export interface DownloadLabels {
  readonly keys: DownloadLabelKeys;
  readonly values: Readonly<Record<string, number>>;
}

/**
 * Each button's key: the CSV's and the Excel's, and `holds` — what the CSV holds, in the words the CSV button says after
 * «Scarica CSV:» («23.636 righe · 3.289 righe di negozi scartati escluse»). The CSV button wears the same words whichever dialect
 * it writes (`docs/adr/9072`, addendum of 2026-10-23), and the small link to the other dialect says what its file holds with
 * `holds`: the two CSVs are one table, so they say the same rows (`docs/adr/9074`). Required, so a caller that words its buttons
 * can never leave the link to a figure of its own — the owner's report of 2026-10-24: «CSV per decimali con ,: tutte le righe
 * (26.925)» beside a CSV that left 3.289 dropped-door rows out.
 */
export type DownloadLabelKeys = Readonly<Record<'csv' | 'xlsx' | 'holds', string>>;

/** {@link DownloadLabels} for a table of `T`: the figures are read from the rows the file is made of. */
export interface DownloadLabelling<T> {
  readonly keys: DownloadLabelKeys;
  readonly values: (rows: readonly T[]) => Readonly<Record<string, number>>;
}

export interface RowCapNoticeProps {
  readonly shown: number;
  readonly total: number;
  /**
   * The export of THIS table — every row, in the order the table draws them. A function so
   * the request is only built when the planner asks for it.
   */
  readonly exportRequest: () => ExportRequest | Promise<ExportRequest>;
  /**
   * The table already says, in its own words, how many rows it is drawing of how many — the
   * consolidation option directory, whose sentence also names the order and the search. The
   * notice then draws the download alone rather than a second count beside the first.
   */
  readonly countStated?: boolean;
  /**
   * Offer the download even when the table is not cut — a table whose CSV is a working
   * artefact in its own right (the elasticity explorer's selection), not only the rest of a
   * long list. The count sentence still appears only when rows are not drawn.
   */
  readonly always?: boolean;
  /**
   * Which rows the download holds, said on the buttons (production visual audit 2026-10-10, A7):
   * `all` — «tutte le righe (23.352)», the rows behind the cap; `shown` — «le righe mostrate (50)»,
   * the rows the table draws. Unset: `all` when the table says its own count of a cut list (a ranked
   * list's «prime 50 di 23.352» — `countStated`), and the plain label otherwise.
   */
  readonly scope?: 'all' | 'shown';
  /** What the buttons say the files hold, in place of the row count (see {@link DownloadLabels}). */
  readonly labels?: DownloadLabels;
  /**
   * The export's columns, when it explains them (`docs/adr/9045`): a «Column guide» download beside
   * the CSV and the Excel — one row per column, its header, machine name, unit and meaning. Built
   * from the columns alone; it reads nothing.
   */
  readonly columnGuide?: ColumnGuideSource;
}

/**
 * The formats every table downloads in — Excel (the primary action) and CSV, the same file twice (DEC-98, ADR 9010 as amended
 * 2026-10-01) — and, beside the CSV, a small link to the OTHER CSV dialect: `;` between fields and a decimal comma for Excel in
 * Italian or Spanish, or the raw `,` and decimal point for other systems (owner rulings 2026-10-22 and 2026-10-23, `docs/adr/9072`).
 * Which of the two the «CSV» button writes follows the computer's regional number format (`src/export/regional.ts`).
 * All come from the one request the caller builds, so the rows, columns, order, filters and provenance columns are identical;
 * only the encoding differs (`src/export/table.ts`).
 */
export const TABLE_DOWNLOAD_FORMATS = ['xlsx', 'csv'] as const;
/** What a download can write: the declared formats and the CSV's second dialect. */
export type TableDownloadFormat = (typeof TABLE_DOWNLOAD_FORMATS)[number] | 'csv-decimal-comma';

/**
 * A download row: Excel first and primary, the «CSV» button in the dialect the computer wants, and the other dialect as a link
 * (`other`). Worked out once per mount — the computer's number format does not change under the page.
 */
export function useDownloadRow<F extends string>(formats: readonly F[]): DownloadArrangement<F> {
  return useMemo(() => arrangeDownloads(formats), [formats]);
}

interface DownloadKeys { readonly download: string; readonly downloadAll: string; readonly downloadShown: string; readonly preparing: string; readonly failed: string }

/** What the CSV holds, for the link to the other dialect: the same scope as the CSV button's words (`docs/adr/9074`). */
const HOLDS_KEYS = { download: 'table.rowCap.holds', downloadAll: 'table.rowCap.holdsAll', downloadShown: 'table.rowCap.holdsShown' } as const;

const DOWNLOAD_KEYS: Record<TableDownloadFormat, DownloadKeys> = {
  csv: {
    download: 'table.rowCap.download', downloadAll: 'table.rowCap.downloadAll', downloadShown: 'table.rowCap.downloadShown',
    preparing: 'table.rowCap.preparing', failed: 'table.rowCap.failed',
  },
  // The CSV button wears the CSV's words whichever dialect it writes; only its busy and failed words name the dialect.
  'csv-decimal-comma': {
    download: 'table.rowCap.download', downloadAll: 'table.rowCap.downloadAll', downloadShown: 'table.rowCap.downloadShown',
    preparing: 'table.rowCap.preparingDecimalComma', failed: 'table.rowCap.failedDecimalComma',
  },
  xlsx: {
    download: 'table.rowCap.downloadXlsx', downloadAll: 'table.rowCap.downloadAllXlsx', downloadShown: 'table.rowCap.downloadShownXlsx',
    preparing: 'table.rowCap.preparingXlsx', failed: 'table.rowCap.failedXlsx',
  },
};

/** Each format's words while it is prepared and when it failed — for a caller that draws its own buttons. */
export const TABLE_DOWNLOAD_PREPARING: Readonly<Record<TableDownloadFormat, string>> = {
  csv: DOWNLOAD_KEYS.csv.preparing, 'csv-decimal-comma': DOWNLOAD_KEYS['csv-decimal-comma'].preparing, xlsx: DOWNLOAD_KEYS.xlsx.preparing,
};
export const TABLE_DOWNLOAD_FAILED: Readonly<Record<TableDownloadFormat, string>> = {
  csv: DOWNLOAD_KEYS.csv.failed, 'csv-decimal-comma': DOWNLOAD_KEYS['csv-decimal-comma'].failed, xlsx: DOWNLOAD_KEYS.xlsx.failed,
};

/**
 * The decimal-comma CSV's tooltip (`docs/adr/9072`): it opens directly in Excel set to Italian or
 * Spanish. Spread onto that format's button; nothing on the others.
 */
export const DECIMAL_COMMA_HINT_KEY = 'export.decimalComma.hint';
export const RAW_CSV_HINT_KEY = 'export.csvRaw.hint';

export function formatHint(format: string, t: (key: string) => string): { readonly title?: string } {
  if (format === 'csv-decimal-comma') return { title: t(DECIMAL_COMMA_HINT_KEY) };
  // The «CSV» button writes either dialect, so each says which it is (addendum of 2026-10-23).
  return format === 'csv' ? { title: t(RAW_CSV_HINT_KEY) } : {};
}

export interface TableDownload {
  /** The format being prepared, or `null`. */
  readonly busy: TableDownloadFormat | null;
  /** How far it has got (`./download-progress.tsx`); `null` while nothing is prepared. */
  readonly progress: DownloadProgressState | null;
  /** The format whose file could not be produced, or `null`. */
  readonly failed: TableDownloadFormat | null;
  /**
   * Why it could not, for a caller that words one cause apart (a file that arrived incomplete, platform ADR 6201);
   * `null` while nothing failed. Never shown verbatim.
   */
  readonly failure: unknown;
  readonly download: (format: TableDownloadFormat) => Promise<void>;
}

/**
 * One table's download, in either format, through `runExport` — so provenance, raw numbers and
 * the missing-data states survive exactly as on the route's own export (G2.4). The request is
 * built when the planner asks, once per click, for the format asked — a builder may make a CSV differently from a workbook
 * (a run's whole file streams into a CSV; a workbook is held whole). The two CSV dialects are asked for the same request.
 *
 * **Its progress** (`docs/adr/9074`): «preparing», then the rows the builder read (the pages any collection walk reads, or what
 * the builder reports through `reporter`), then the rows written into the file — of the CSV's `rowCount` when the request
 * states one. **One at a time**: a second press while a file is prepared — a double click — starts nothing.
 */
export function useTableDownload(
  exportRequest: (format: TableDownloadFormat, reporter: DownloadReporter) => ExportRequest | Promise<ExportRequest>,
): TableDownload {
  const { t, i18n } = useTranslation();
  const [busy, setBusy] = useState<TableDownloadFormat | null>(null);
  const [failed, setFailed] = useState<TableDownloadFormat | null>(null);
  const [failure, setFailure] = useState<unknown>(null);
  const { progress, track } = useDownloadProgress();
  // Set before the first await, so a second click in the same frame — before `busy` disables the button — is refused.
  const running = useRef(false);
  const download = async (format: TableDownloadFormat): Promise<void> => {
    if (running.current) return;
    running.current = true;
    setBusy(format);
    setFailed(null);
    setFailure(null);
    try {
      const outcome = await track(async (reporter) => {
        const request = await exportRequest(format, reporter);
        return runExport(request, {
          format,
          translate: (key: string, values?: Readonly<Record<string, unknown>>) => t(key, values ?? {}),
          locale: i18n.language,
          onProgress: writtenProgress(reporter, format, request),
        });
      });
      saveOutcome(outcome);
    } catch (cause) {
      // Not surfaced verbatim, for the reason `KpiReportingTile` gives: an exception's
      // message is engine prose, and the planner can act only on "it did not work".
      setFailed(format);
      setFailure(cause);
    } finally {
      running.current = false;
      setBusy(null);
    }
  };
  return { busy, progress, failed, failure, download };
}

/**
 * The toolbar row above a table: how many rows are drawn, and the downloads for all of them —
 * the CSV, the same CSV with a decimal comma for Excel in Italian or Spanish, and the Excel workbook
 * of the same rows.
 * Callers render it directly BEFORE the table, so it is read and tabbed to before the rows.
 *
 * Renders nothing when the table is not cut — a notice over every short table would teach
 * the planner to skip it.
 */
export function RowCapNotice({
  shown, total, exportRequest, countStated = false, always = false, scope, columnGuide, labels,
}: RowCapNoticeProps): React.JSX.Element | null {
  const { t } = useTranslation();
  const { busy, progress, failed, download } = useTableDownload(() => exportRequest());
  const row = useDownloadRow(TABLE_DOWNLOAD_FORMATS);
  const cut = total > shown;
  if (!cut && !always) return null;
  const said = scope ?? (cut && countStated ? 'all' : undefined);
  const scoped = (keys: { readonly download: string; readonly downloadAll: string; readonly downloadShown: string }): string => (
    said === 'all' ? keys.downloadAll : said === 'shown' ? keys.downloadShown : keys.download
  );
  const label = (words: (typeof TABLE_DOWNLOAD_FORMATS)[number]): string => (labels !== undefined ? labels.keys[words] : scoped(DOWNLOAD_KEYS[words]));
  const values = { total, ...(labels?.values ?? {}) };
  // What both CSVs hold, worded in one place: the link to the other dialect says it after its own words (`docs/adr/9074`).
  const holds = t(labels !== undefined ? labels.keys.holds : scoped(HOLDS_KEYS), values);

  return (
    // `data-table-toolbar`: the «Documento» page model hides it in print (ADR 9053) — a printed
    // table has no download.
    <div className="flex flex-wrap items-center justify-end gap-2" data-table-toolbar="">
      {cut && !countStated && (
        <p className="text-caption text-ink-secondary">
          {t('table.rowCap.showing', { shown, total })}
        </p>
      )}
      {row.buttons.map(({ format, words, primary }) => (
        <Button
          key={format}
          variant={primary ? 'primary' : 'secondary'}
          size="sm"
          onClick={() => { void download(format); }}
          loading={busy === format}
          disabled={busy !== null && busy !== format}
          icon={<Download size={14} aria-hidden />}
          data-export-format={format}
          {...formatHint(format, t)}
        >
          {t(busy === format ? DOWNLOAD_KEYS[format].preparing : label(words), values)}
        </Button>
      ))}
      {row.other !== null && (
        <OtherCsvLink
          format={row.other}
          onDownload={() => { void download(row.other!); }}
          busy={busy === row.other}
          disabled={busy !== null && busy !== row.other}
          preparing={DOWNLOAD_KEYS[row.other].preparing}
          holds={holds}
          data-export-format={row.other}
        />
      )}
      {columnGuide !== undefined && (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => saveOutcome(runColumnGuide(columnGuide, {
            translate: (key: string, values?: Readonly<Record<string, unknown>>) => t(key, values ?? {}),
          }))}
          disabled={busy !== null}
          icon={<BookOpen size={14} aria-hidden />}
          title={t('export.columnGuide.downloadHint')}
          data-export-format="columns"
        >
          {t('export.columnGuide.download')}
        </Button>
      )}
      {busy !== null && (
        <div className="flex basis-full justify-end">
          <DownloadProgressLine progress={progress} />
        </div>
      )}
      {failed !== null && <p role="status" className="text-caption text-status-critical" data-download-failed={failed}>{t(DOWNLOAD_KEYS[failed].failed)}</p>}
    </div>
  );
}
